// src/pages/Profile.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Profile.css';

const Profile = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.profile-card, .profile-block').forEach((el) => {
      el.classList.add('fade-in');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <Header />

      <main className="profile-page">
        <section className="profile-hero">
          <div className="container">
            <Link to="/#about" className="back-link">&larr; Back to About</Link>
            <h1 className="section-title">The Artists' Journey</h1>
            <h3 className="section-subtitle">"Rejoicing in Rhythms"</h3>
            <h4 className="artists-names">Srujana &amp; Sree Keerthi Valiveti</h4>
          </div>
        </section>

        {/* Section 1: shared narrative */}
        <section className="profile-section profile-section--shared">
          <div className="container">
            <div className="profile-block">
              <h2>Artistic Prowess</h2>
              <p>
                From the iconic Narada Gana Sabha to the regal settings of Sirifort Delhi,
                their artistry — even in group shows — has marked a strong presence. They
                have been performing with their Guru in various ballets and shows for the
                last 12 years actively, and have been acknowledged by luminaries ranging
                from Chief Ministers and Governors to Carnatic legends, enchanting audiences
                at events such as the G20 Summit and corporate galas. Their dance has
                transcended boundaries, captivating hearts at cultural festivals and events
                like Kalasagaram, Doordarshan, and Nadaneerajanam.
              </p>
              <p>
                They have performed on prestigious stages in colleges and universities such
                as IIM Bangalore, FDDI, NIFT, St. Francis, and SNIST, Hyderabad. Their
                Arangetram in 2020 marked a significant milestone, symbolizing their mastery
                and dedication to their craft — they have been performing duos since. They
                have choreographed projects for the Telugu film industry like
                <em> Varahi Sharanu</em>, performed in major festivals like the Nainital
                Literary Festival, and hosted Kalaripayattu workshops.
              </p>
            </div>

            <div className="profile-block">
              <h2>A Global Footprint</h2>
              <p>
                Srujana and Sree Keerthi have also performed in international theaters,
                leaving audiences spellbound with their mesmerizing performances. Their
                artistry knows no bounds, as evidenced by their performances at esteemed
                institutions like Skyline University in Sharjah, UAE.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Srujana */}
        <section id="srujana" className="profile-section profile-section--srujana">
          <div className="container">
            <div className="profile-card">
              <header className="profile-card__header">
                <h2>Srujana Valiveti</h2>
                <p className="profile-card__tag">The elder sibling — analytical, entrepreneurial, expressive</p>
              </header>

              <article className="profile-block">
                <h3>Multifaceted Mastery</h3>
                <p>
                  As the elder of the duo, Srujana brings the steadiness of an engineer,
                  the strategic clarity of an MBA graduate (IIM Bangalore), and the founder's
                  drive of her startup, GetYoSpace, to every performance. A certified yoga
                  practitioner trained in Kalaripayattu, she fuses analytical precision
                  with deeply felt artistic expression — a combination that lends her
                  Bharatanatyam its distinct, poised authority.
                </p>
              </article>

              <article className="profile-block">
                <h3>Inspirational Leadership</h3>
                <p>
                  As the elder sister, Srujana naturally takes the lead in shaping the
                  ensemble's artistic vision and the school's direction. She mentors
                  students with patience and intent, anchors the duo's choreographic
                  decisions, and represents Kalavaanya at academic and cultural forums
                  where rigour and articulation matter.
                </p>
              </article>

              <article className="profile-block">
                <h3>Media Spotlight</h3>
                <p>
                  Srujana's work has been featured across cultural telecasts on Doordarshan
                  and Nadaneerajanam, in film-industry choreography credits including
                  <em> Varahi Sharanu</em>, and in coverage of marquee events like the
                  G20 Summit and the Nainital Literary Festival — placing her among a
                  recognised generation of young Bharatanatyam voices.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Section 2: Keerthi */}
        <section id="keerthi" className="profile-section profile-section--keerthi">
          <div className="container">
            <div className="profile-card">
              <header className="profile-card__header">
                <h2>Sree Keerthi Valiveti</h2>
                <p className="profile-card__tag">The younger sibling — aesthetic, spirited, finely tuned</p>
              </header>

              <article className="profile-block">
                <h3>Multifaceted Mastery</h3>
                <p>
                  As the younger of the duo, Sree Keerthi brings a fashion designer's eye
                  for line, colour, and silhouette to the stage. A certified yoga
                  practitioner trained in Kalaripayattu, she focuses on the aesthetic and
                  technical fineness of dance — costuming, framing, and the lyrical
                  precision of mudras — giving the duo's work its visual signature.
                </p>
              </article>

              <article className="profile-block">
                <h3>Inspirational Leadership</h3>
                <p>
                  Keerthi leads the school's pedagogy and student-experience side, pairing
                  warm, accessible teaching with high standards. Her workshops — including
                  Kalaripayattu sessions — invite younger learners into the discipline
                  without losing its depth, and her presence on stage with her sister
                  embodies a generational continuity rooted in shared training.
                </p>
              </article>

              <article className="profile-block">
                <h3>Media Spotlight</h3>
                <p>
                  Sree Keerthi has shared the camera and the stage with her sister across
                  Doordarshan and Nadaneerajanam telecasts, the G20 Summit, Kalasagaram,
                  the Nainital Literary Festival, and international engagements at
                  Skyline University, Sharjah — a body of work that continues to expand
                  the duo's reach.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="profile-cta">
          <div className="container">
            <Link to="/#about" className="btn btn-secondary">Back to About</Link>
            <Link to="/#contact" className="btn btn-primary">Get in Touch</Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Profile;
